import { defineStore } from 'pinia';
import axios from 'axios';
import { onEvent } from '@/utils/eventBus';
import { useAuthStore } from './auth';
import { countRightToWorkAttentionRows } from '@/utils/rightToWork';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_LIVE_REFRESH_INTERVAL_MS = 20_000;
const requestState = new WeakMap();
const runtimeFor = (store) => {
  if (!requestState.has(store)) requestState.set(store, { pending: null, revision: 0, unsubscribe: null });
  return requestState.get(store);
};

const defaultEligibleForStatus = (rightToWorkStatus) =>
  rightToWorkStatus === 'valid' || rightToWorkStatus === 'expiring_soon';

const toLegacyEligible = (value) => {
  if (value === 'ok') return true;
  if (value === 'not_ok') return false;
  return null;
};

const normalizeEligibilityRow = (row = {}) => {
  const rightToWorkStatus = String(row?.rightToWorkStatus || 'missing');
  const legacyEligible = toLegacyEligible(row?.finalStatus);
  const eligible = typeof row?.eligible === 'boolean'
    ? row.eligible
    : (legacyEligible !== null ? legacyEligible : defaultEligibleForStatus(rightToWorkStatus));

  return {
    ...row,
    supplierId: row?.supplierId ?? null,
    supplierDocumentId: row?.supplierDocumentId ?? null,
    rightToWorkStatus,
    excluded: Boolean(row?.excluded || row?.workPermissionsExcluded),
    eligible,
  };
};

const buildSummaryFromRows = (rows = [], fallbackSummary = {}) => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const activeRows = sourceRows.filter((row) => !row?.excluded);

  return {
    ...fallbackSummary,
    totalSuppliers: activeRows.length,
    valid: activeRows.filter((row) => row?.rightToWorkStatus === 'valid').length,
    eligible: activeRows.filter((row) => row?.eligible === true).length,
    needsVerification: activeRows.filter((row) => row?.rightToWorkStatus === 'needs_verification').length,
    missing: activeRows.filter((row) => row?.rightToWorkStatus === 'missing').length,
    expiringSoon: activeRows.filter((row) => row?.rightToWorkStatus === 'expiring_soon').length,
    notStarted: activeRows.filter((row) => row?.rightToWorkStatus === 'not_started').length,
    expired: activeRows.filter((row) => row?.rightToWorkStatus === 'expired').length,
    excluded: sourceRows.length - activeRows.length,
  };
};

const normalizeReport = (entry) => {
  const attributes = entry?.attributes || entry || {};
  const rowsRaw = Array.isArray(attributes.eligibilitySnapshot)
    ? attributes.eligibilitySnapshot
    : [];
  const eligibilitySnapshot = rowsRaw.map(normalizeEligibilityRow);

  return {
    id: attributes.id ?? entry?.id ?? 'live',
    documentId: attributes.documentId ?? entry?.documentId ?? 'live',
    name: attributes.name ?? 'Work permissions report',
    snapshotDate: attributes.snapshotDate ?? null,
    status: attributes.status ?? 'live',
    summary: buildSummaryFromRows(eligibilitySnapshot, attributes.summary ?? {}),
    eligibilitySnapshot,
    metadata: attributes.metadata ?? {},
    createdAt: attributes.createdAt ?? null,
    updatedAt: attributes.updatedAt ?? null,
    raw: entry,
  };
};

export const useWorkPermissionsReportsStore = defineStore('workPermissionsReports', {
  state: () => ({
    liveReport: null,
    isLoading: false,
    error: null,
    livePollingIntervalId: null,
    livePollingConsumers: 0,
  }),
  getters: {
    reportRows(state) {
      return Array.isArray(state.liveReport?.eligibilitySnapshot)
        ? state.liveReport.eligibilitySnapshot
        : [];
    },
    // Expired and Expiring soon suppliers.
    attentionCount(state) {
      return countRightToWorkAttentionRows(state.liveReport?.eligibilitySnapshot);
    },
  },
  actions: {
    authHeaders() {
      const authStore = useAuthStore();
      return authStore.token ? authStore.requestHeaders.headers : {};
    },

    setLiveReport(report) {
      this.liveReport = normalizeReport(report);
      return this.liveReport;
    },

    async fetchLiveReport({ force = false } = {}) {
      const runtime = runtimeFor(this);
      if (runtime.pending && !force) return runtime.pending;
      const revision = ++runtime.revision;
      this.isLoading = true;
      this.error = null;
      const request = (async () => {
        try {
          const response = await axios.get(`${STRAPI_URL}/api/work-permissions-reports/live`, {
            headers: this.authHeaders(),
          });
          if (revision !== runtime.revision) return this.liveReport;
          return this.setLiveReport(response?.data?.data || {});
        } catch (error) {
          if (revision === runtime.revision) {
            this.error = error?.response?.data?.error?.message || error?.message || 'Failed to load live work permissions report';
          }
          throw error;
        } finally {
          if (revision === runtime.revision) {
            this.isLoading = false;
            runtime.pending = null;
          }
        }
      })();
      runtime.pending = request;
      return request;
    },

    startLivePolling({
      intervalMs = DEFAULT_LIVE_REFRESH_INTERVAL_MS,
      immediate = true,
    } = {}) {
      this.livePollingConsumers += 1;
      const runtime = runtimeFor(this);
      if (!runtime.unsubscribe) {
        runtime.unsubscribe = onEvent('catalog:changed', ({ catalogName } = {}) => {
          if (catalogName && String(catalogName).trim().toLowerCase() !== 'suppliers') return;
          void this.fetchLiveReport({ force: true }).catch(() => {});
        });
      }

      if (immediate) {
        void this.fetchLiveReport().catch(() => {});
      }

      if (this.livePollingIntervalId !== null || typeof window === 'undefined') return;

      const requestedIntervalMs = Number(intervalMs);
      const normalizedIntervalMs = Number.isFinite(requestedIntervalMs) && requestedIntervalMs > 0
        ? requestedIntervalMs
        : DEFAULT_LIVE_REFRESH_INTERVAL_MS;

      this.livePollingIntervalId = window.setInterval(() => {
        void this.fetchLiveReport().catch(() => {});
      }, normalizedIntervalMs);
    },

    stopLivePolling() {
      this.livePollingConsumers = Math.max(0, this.livePollingConsumers - 1);
      if (this.livePollingConsumers > 0) return;
      const runtime = runtimeFor(this);
      runtime.unsubscribe?.();
      runtime.unsubscribe = null;

      if (typeof window !== 'undefined') {
        window.clearInterval(this.livePollingIntervalId);
      }
      this.livePollingIntervalId = null;
    },

    async updateLiveRow(supplierId, payload = {}) {
      if (!supplierId) throw new Error('Missing supplier identifier');

      try {
        this.error = null;
        const response = await axios.put(
          `${STRAPI_URL}/api/work-permissions-reports/live/suppliers/${encodeURIComponent(
            supplierId,
          )}`,
          payload,
          { headers: this.authHeaders() },
        );
        const runtime = runtimeFor(this);
        runtime.revision++;
        runtime.pending = null;
        this.isLoading = false;
        return this.setLiveReport(response?.data?.data || {});
      } catch (error) {
        this.error =
          error?.response?.data?.error?.message ||
          error?.message ||
          'Failed to update live work permissions row';
        throw error;
      }
    },

    async fetchReportById() {
      return this.fetchLiveReport();
    },
  },
});
